import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import * as authAPI from '../services/operations/authAPI';
import { GOOGLE_CLIENT_ID } from '../services/api';

// Google Client ID imported from api.js

// console.log('Google Client ID loaded:', GOOGLE_CLIENT_ID);
let isGoogleInitialized = false;

export const useGoogleLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleGoogleResponse = useCallback(async (response) => {
    // console.log('🟢 Google response received:', { credential: response.credential ? 'Present' : 'Missing', credentialLength: response.credential?.length, fullResponse: response});

    try {
      // console.log('🟡 Sending request to backend...');
      const res = await authAPI.googleLogin(response.credential);
      // console.log('🟢 Backend response received:', res);

      if (res && res.success) {
        const returnedUser = res.user || res.data?.user;
        const tempToken = res.tempToken;

        // If this is a first-time Google signup OR profile is incomplete
        if (res.newUser || res.incompleteProfile) {
          toast.info(res.message || 'Please complete your profile');
          // Do NOT log the user in yet. Redirect to SignUp to collect missing fields and pass tempToken.
          navigate('/SignUp', { state: { googleUser: returnedUser, googleSignup: true, missingFields: [], tempToken } });
          return;
        }

        // For existing Google users with complete profile: log them in and navigate to profile
        toast.success(res.message || 'Google login successful');
        login(returnedUser);
        navigate('/profile');
      } else {
        console.error('🔴 Backend login failed:', res);
        const msg = res?.message || 'Google login failed';
        toast.error(msg);
        const lower = String(msg).toLowerCase();
        if (lower.includes('password') || lower.includes('use password') || (lower.includes('exists') && !lower.includes('google'))) {
          navigate('/login');
        }
      }
    } catch (error) {
      console.error('🔴 Google login network/server error:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        fullError: error
      });
      toast.error(`Google login failed: ${error.response?.data?.message || error.message}`);
    }
  }, [login, navigate]);

  const initializeGoogleLogin = useCallback(() => {
    // console.log('🟡 initializeGoogleLogin called', { googleAvailable: !!window.google, alreadyInitialized: isGoogleInitialized, clientId: GOOGLE_CLIENT_ID });

    if (window.google && !isGoogleInitialized) {
      // console.log('🟡 Initializing Google with Client ID:', GOOGLE_CLIENT_ID);

      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        isGoogleInitialized = true;
        // console.log('🟢 Google Identity initialized successfully');
      } catch (error) {
        console.error('🔴 Google initialization error:', error);
      }
    } else if (isGoogleInitialized) {
      // console.log('🟡 Google already initialized');
    } else {
      // console.log('🔴 Google services not available for initialization');
    }
  }, [handleGoogleResponse]);



  const handleGoogleLogin = useCallback(() => {
    // console.log('handleGoogleLogin called');

    if (!window.google) {
      console.error('Google services not loaded');
      toast.error('Google services not loaded. Please refresh the page and try again.');
      return;
    }

    // Ensure initialization
    if (!isGoogleInitialized) {
      // console.log('Google not initialized, initializing now...');
      initializeGoogleLogin();
      // Wait a bit for initialization to complete
      setTimeout(() => handleGoogleLogin(), 500);
      return;
    }

    // console.log('Triggering Google prompt...');
    try {
      window.google.accounts.id.prompt((notification) => {
        // console.log('Google prompt notification:', { isNotDisplayed: notification.isNotDisplayed(), isSkippedMoment: notification.isSkippedMoment(), notDisplayedReason: notification.getNotDisplayedReason(), skippedReason: notification.getSkippedReason() });
      });
    } catch (error) {
      console.error('Google prompt error:', error);
      toast.error('Google login failed. Please try again.');
    }
  }, [initializeGoogleLogin]);

  const renderGoogleButton = useCallback((elementId) => {
    if (window.google) {
      initializeGoogleLogin();
      window.google.accounts.id.renderButton(
        document.getElementById(elementId),
        {
          theme: 'filled_blue',
          size: 'large',
          width: '100%',
          text: 'signin_with',
          shape: 'rectangular'
        }
      );
    }
  }, [initializeGoogleLogin]);

  const cleanup = useCallback(() => {
    if (window.google) {
      try {
        window.google.accounts.id.cancel();
        window.google.accounts.id.disableAutoSelect();
        isGoogleInitialized = false; // Reset initialization flag
      } catch (error) {
        // Safe to ignore
      }
    }
  }, []);

  const directGoogleLogin = useCallback(() => {
    if (window.google && isGoogleInitialized) {
      // console.log('🟡 Creating temporary Google button...');
      // Create a temporary container for Google button
      const tempDiv = document.createElement('div');
      tempDiv.id = 'temp-google-button';
      tempDiv.style.display = 'none';
      document.body.appendChild(tempDiv);

      try {
        // console.log('🟡 Rendering Google button...');
        window.google.accounts.id.renderButton(tempDiv, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          width: 200,
        });

        // console.log('🟡 Button rendered, simulating click...');
        // Simulate click on the Google button
        setTimeout(() => {
          const button = tempDiv.querySelector('div[role="button"]');
          // console.log('🟡 Found button:', !!button);
          if (button) {
            // console.log('🟡 Clicking Google button...');
            button.click();
          } else {
            // console.log('🔴 No button found to click');
          }
          // Clean up
          if (document.body.contains(tempDiv)) {
            document.body.removeChild(tempDiv);
          }
        }, 100);

      } catch (error) {
        // console.log('🔴 Direct Google login error:', error);
        if (document.body.contains(tempDiv)) {
          document.body.removeChild(tempDiv);
        }
      }
    } else {
      // console.log('🔴 Google not ready:', { googleAvailable: !!window.google, isInitialized: isGoogleInitialized });
    }
  }, []);

  return {
    handleGoogleLogin,
    renderGoogleButton,
    initializeGoogleLogin,
    cleanup,
    directGoogleLogin
  };
};